import { Container, Graphics } from 'pixi.js';
import { place, type RoomContext, type RoomDecorator } from './index';
import { mix, lighten, darken, type ThemeArt } from '../theme-art';
import { AO } from '../shading';
import { registerScenery } from '../scenery';

/**
 * GAME ROOM — the office's loud, joyful "play" space (x 40..440, y 840..1160; centre 240,1000).
 *
 * REDESIGNED for ORDER + STYLE (Creative Director: kill the clutter — no scattered plants, no stray
 * rug-blobs — and make every seat sittable). It still reads as FUN the instant you walk in (neon, arcade,
 * party lights), but it is now TIDY and ALIGNED into three deliberate zones on a clean east–west axis:
 *
 *   1. ARCADE NOOK (centre): the hero cabinet at (240,1000) with a gaming chair pulled up at (320,1000)
 *      facing LEFT (west) toward it. ONE soft glow pool grounds just this nook — the only floor glow.
 *   2. TV LOUNGE (west wall): a neon sign + console TV with a plump low sofa parked in front, flanked by
 *      two tidy beanbags, ALL grounded on the room's ONE neat rug. A cosy "watch together" corner.
 *   3. GAMES TABLE (south): a single air-hockey table as the centrepiece, squared up below the nook.
 *
 * The floor stays walkable: the N door (top, x 160..320 → server room) and the E door (right, y 860..980
 * → reception) lanes are kept clear, with an open path between them. Greenery is exactly TWO plants, one
 * per back corner; wall décor is two neon posters + one tidy string of fairy lights high on the west wall.
 *
 * SITTABLE SEATS (every soft seat is a real affordance; the decorator's seat-anchor list, in player coords):
 *   • game-room gaming chair  (320,1000) facing WEST — pulled up to the arcade cabinet
 *   • TV-lounge sofa          (104,1000) facing WEST — the "watch together" couch on the rug
 *   • beanbag (north)         (112, 952) facing WEST — flops toward the screen, on the rug
 *   • beanbag (south)         (112,1048) facing WEST — flops toward the screen, on the rug
 *
 * Everything is themed off `art` (+ mix/lighten/darken) so it re-skins per world; the arcade cabinet,
 * air-hockey table, neon sign, gaming chair, fairy lights and beanbag are small room-specific inline
 * builders (this module only — furniture.ts is shared). Built ONCE on map build/re-skin: no per-frame
 * work, no allocation in a loop.
 */

/** Colour helpers, bound once per decorate() so the inline builders read cleanly. */
type Mix = (a: number, b: number, t: number) => number;
type Shade = (hex: number, t: number) => number;

/** A signature game-room palette of bright play colours — vivid, but they still sit happily on any theme. */
const NEON = {
  pink: 0xff5d8f,
  cyan: 0x5ad1ff,
  lime: 0x95e06c,
  gold: 0xffd23f,
  violet: 0x9d6bff,
} as const;

/**
 * A standing ARCADE CABINET — glowing marquee, lit screen with a couple of pixel sprites, an angled
 * control deck with buttons + a joystick nub. `hue` tints the marquee + screen so it reads as a colourful
 * arcade. Local space, contact shadow, body rising above the placement point.
 */
function arcadeCabinet(art: ThemeArt, mix: Mix, lighten: Shade, darken: Shade, hue: number): Container {
  const c = new Container();
  const shell = darken(mix(0x2b2f3a, art.wall, 0.18), 0.05);
  c.addChild(new Graphics().ellipse(0, 16, 20, 6).fill({ color: 0x000000, alpha: 0.24 }));
  c.addChild(
    new Graphics()
      // body shell (marquee head → screen chest → control deck → base)
      .roundRect(-18, -64, 36, 80, 6)
      .fill(shell)
      .roundRect(-18, -64, 36, 6, 6)
      .fill({ color: 0xffffff, alpha: 0.08 })
      // bottom ambient-occlusion band (away from the top-left key light)
      .roundRect(-18, 4, 36, 12, 6)
      .fill({ color: 0x000000, alpha: AO * 0.7 })
      // glowing marquee header sign
      .roundRect(-16, -60, 32, 12, 3)
      .fill({ color: hue, alpha: 0.92 })
      .roundRect(-16, -60, 32, 4, 3)
      .fill({ color: 0xffffff, alpha: 0.3 })
      // the lit screen
      .roundRect(-14, -44, 28, 22, 2)
      .fill(0x10131b)
      .roundRect(-12, -42, 24, 18, 1.5)
      .fill({ color: lighten(hue, 0.12), alpha: 0.85 })
      // a couple of pixel "sprites" on the screen for life
      .rect(-7, -36, 4, 4)
      .fill(0xffffff)
      .rect(4, -31, 4, 4)
      .fill({ color: NEON.gold, alpha: 0.95 })
      // angled control deck + two arcade buttons + a joystick nub
      .roundRect(-16, -18, 32, 10, 2)
      .fill(darken(shell, 0.12))
      .circle(-7, -13, 2.4)
      .fill(NEON.pink)
      .circle(1, -13, 2.4)
      .fill(NEON.cyan)
      .circle(-12, -13, 1.6)
      .fill(0x14171f),
  );
  // a soft coloured glow bleeding from the screen onto the floor
  c.addChild(new Graphics().ellipse(0, 14, 22, 8).fill({ color: hue, alpha: 0.1 }));
  return c;
}

/**
 * An AIR-HOCKEY TABLE — the room's lower-floor centrepiece. A glossy dark playfield with a painted centre
 * line + face-off circle, cushioned side rails, a goal mouth at each end, two coloured mallets and the
 * puck mid-table. Origin at its centre; `w`,`h` set the footprint.
 */
function airHockey(art: ThemeArt, mix: Mix, lighten: Shade, darken: Shade, w: number, h: number): Container {
  const c = new Container();
  const rail = mix(0x3a4252, art.wall, 0.22);
  const bed = darken(mix(0x1c2230, art.accent, 0.1), 0.04);
  const hw = w / 2;
  const hh = h / 2;
  c.addChild(new Graphics().ellipse(0, hh + 6, hw - 6, 14).fill({ color: 0x000000, alpha: 0.22 }));
  const g = new Graphics()
    // rail frame
    .roundRect(-hw, -hh, w, h, 10)
    .fill(rail)
    .roundRect(-hw, -hh, w, 5, 10)
    .fill({ color: 0xffffff, alpha: 0.12 })
    // glossy playfield bed (inset)
    .roundRect(-hw + 10, -hh + 10, w - 20, h - 20, 6)
    .fill(bed)
    .roundRect(-hw + 10, -hh + 10, w - 20, 6, 6)
    .fill({ color: 0xffffff, alpha: 0.06 })
    // painted centre line + face-off circle
    .rect(-1, -hh + 12, 2, h - 24)
    .fill({ color: NEON.cyan, alpha: 0.55 })
    .circle(0, 0, 13)
    .stroke({ color: NEON.cyan, width: 2, alpha: 0.5 })
    // goal mouths at each end
    .roundRect(-hw + 8, -16, 4, 32, 2)
    .fill({ color: NEON.pink, alpha: 0.8 })
    .roundRect(hw - 12, -16, 4, 32, 2)
    .fill({ color: NEON.gold, alpha: 0.8 });
  c.addChild(g);
  // two mallets + the puck
  c.addChild(
    new Graphics()
      .circle(-hw + 30, 0, 7)
      .fill(NEON.pink)
      .circle(-hw + 30, 0, 3.4)
      .fill(darken(NEON.pink, 0.25))
      .circle(hw - 30, 0, 7)
      .fill(NEON.gold)
      .circle(hw - 30, 0, 3.4)
      .fill(darken(NEON.gold, 0.25))
      // puck just off centre
      .circle(8, -4, 4.2)
      .fill(lighten(0x14171f, 0.25))
      .circle(7, -5, 1.4)
      .fill({ color: 0xffffff, alpha: 0.4 }),
  );
  return c;
}

/**
 * A neon WALL SIGN — four glowing tube "bars" with soft bloom haloes on a dim backing board. Bolted to
 * the wall (sits flat, no contact shadow). `w` controls the run of tubes.
 */
function neonSign(w = 120): Container {
  const c = new Container();
  const hw = w / 2;
  // a faint backing board so the tubes read against the wall
  c.addChild(new Graphics().roundRect(-hw - 6, -20, w + 12, 36, 6).fill({ color: 0x14171f, alpha: 0.55 }));
  const tubes = [NEON.pink, NEON.cyan, NEON.lime, NEON.gold];
  const g = new Graphics();
  for (let i = 0; i < 4; i++) {
    const x = -hw + 14 + i * (w / 4);
    const col = tubes[i]!;
    g.roundRect(x - 7, -14, 14, 24, 7).fill({ color: col, alpha: 0.18 }); // bloom halo
    g.roundRect(x - 3, -12, 6, 20, 3).fill({ color: col, alpha: 0.95 }); // bright tube
    g.roundRect(x - 1.4, -12, 3, 20, 2).fill({ color: 0xffffff, alpha: 0.6 }); // hot core
  }
  c.addChild(g);
  return c;
}

/**
 * A GAMING CHAIR turned to FACE LEFT (west) toward the arcade — a racing-style bucket seat with a tall
 * winged backrest, a head pillow and a contoured base. Drawn in profile so the backrest sits on the RIGHT
 * (east) and the seat opens to the LEFT, reading as "pulled up to the cabinet". `hue` accents the trim.
 */
function gamingChair(art: ThemeArt, mix: Mix, lighten: Shade, darken: Shade, hue: number): Container {
  const c = new Container();
  const body = mix(0x2a2e3a, art.wall, 0.14);
  c.addChild(new Graphics().ellipse(0, 14, 18, 6).fill({ color: 0x000000, alpha: 0.22 }));
  c.addChild(
    new Graphics()
      // pedestal base + post
      .ellipse(2, 12, 13, 5)
      .fill(darken(body, 0.18))
      .rect(0, -2, 4, 14)
      .fill(darken(body, 0.1))
      // seat cushion (opens to the left)
      .roundRect(-12, -6, 26, 12, 5)
      .fill(body)
      .roundRect(-12, -6, 26, 5, 5)
      .fill({ color: 0xffffff, alpha: 0.08 })
      // tall winged backrest on the right (east) side
      .roundRect(8, -42, 14, 42, 7)
      .fill(darken(body, 0.06))
      .roundRect(8, -42, 6, 42, 7)
      .fill({ color: hue, alpha: 0.45 })
      // head pillow
      .roundRect(7, -44, 14, 10, 5)
      .fill(lighten(body, 0.1))
      // accent racing stripes up the back
      .rect(14, -38, 3, 34)
      .fill({ color: hue, alpha: 0.8 })
      .rect(18, -38, 1.6, 34)
      .fill({ color: lighten(hue, 0.2), alpha: 0.7 }),
  );
  return c;
}

/** A floppy BEANBAG — a soft slumped fabric dome you sink into; `hue` picks its colour. */
function beanbag(lighten: Shade, darken: Shade, hue: number): Container {
  const c = new Container();
  c.addChild(new Graphics().ellipse(0, 8, 24, 8).fill({ color: 0x000000, alpha: 0.2 }));
  c.addChild(
    new Graphics()
      .ellipse(0, 2, 24, 14)
      .fill(darken(hue, 0.12))
      .ellipse(0, -4, 20, 12)
      .fill(hue)
      .ellipse(-4, -8, 13, 8)
      .fill(lighten(hue, 0.14))
      .moveTo(-16, 0)
      .bezierCurveTo(-6, -10, 6, -10, 16, 0)
      .stroke({ color: darken(hue, 0.2), width: 1.5, alpha: 0.6 }),
  );
  return c;
}

/**
 * A string of FAIRY LIGHTS — a gently sagging cable with glowing bulbs, slung along a wall to add cosy
 * party warmth up high. Flat against the wall (no contact shadow). `w` is the run; bulbs alternate hue.
 */
function fairyLights(darken: Shade, w: number): Container {
  const c = new Container();
  const hw = w / 2;
  const bulbs = [NEON.gold, NEON.pink, NEON.cyan, NEON.lime, NEON.violet];
  const g = new Graphics();
  const sag = 14;
  const count = Math.max(4, Math.round(w / 28));
  // the cable: a shallow catenary sketched as short chords between sag points
  let prevX = -hw;
  let prevY = 0;
  for (let i = 1; i <= count; i++) {
    const t = i / count;
    const x = -hw + t * w;
    const y = Math.sin(t * Math.PI) * sag;
    g.moveTo(prevX, prevY).lineTo(x, y);
    prevX = x;
    prevY = y;
  }
  g.stroke({ color: darken(0x2a2e3a, 0.1), width: 1.5, alpha: 0.7 });
  c.addChild(g);
  // the bulbs, hanging just below the cable at each sag point
  const bg = new Graphics();
  for (let i = 0; i <= count; i++) {
    const t = i / count;
    const x = -hw + t * w;
    const y = Math.sin(t * Math.PI) * sag;
    const col = bulbs[i % bulbs.length]!;
    bg.circle(x, y + 4, 5).fill({ color: col, alpha: 0.2 }); // bloom
    bg.circle(x, y + 4, 2.4).fill({ color: col, alpha: 0.95 }); // bulb
    bg.circle(x - 0.6, y + 3.2, 1).fill({ color: 0xffffff, alpha: 0.7 }); // hot core
  }
  c.addChild(bg);
  return c;
}

// ── EDITABLE SCENERY REGISTRATION ───────────────────────────────────────────────────────────────────
// Register the room's bespoke FURNITURE so the level editor can re-render/add it. Each lambda reproduces
// the EXACT build its place() call uses (same imported colour helpers + same literal args). Variant args
// (the beanbag hue) are baked into distinct kinds. Painted/architectural backdrop is NOT registered.
registerScenery('arcade-cabinet', (art) => arcadeCabinet(art, mix, lighten, darken, NEON.pink), 'Arcade cabinet', '🕹️');
registerScenery('gaming-chair', (art) => gamingChair(art, mix, lighten, darken, NEON.cyan), 'Gaming chair', '🪑');
registerScenery('beanbag-gold', (_art) => beanbag(lighten, darken, NEON.gold), 'Beanbag (gold)', '🛋️');
registerScenery('beanbag-violet', (_art) => beanbag(lighten, darken, NEON.violet), 'Beanbag (violet)', '🛋️');
registerScenery('air-hockey', (art) => airHockey(art, mix, lighten, darken, 150, 76), 'Air-hockey table', '🏒');

const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { container, zone, art, f, mix, lighten, darken } = ctx;
  const left = zone.x; // 40
  const right = zone.x + zone.width; // 440
  const top = zone.y; // 840
  const bottom = zone.y + zone.height; // 1160
  const cx = ctx.cx; // 240
  const cy = ctx.cy; // 1000

  // ── ONE floor glow only: a single soft pool grounding the arcade nook (no stray rug-blobs). ─────────
  // Low-alpha so it tints the floor without dimming the foreground (a11y floor stays clear, ADR-0012).
  container.addChild(
    new Graphics().ellipse(cx + 40, cy + 2, 116, 48).fill({ color: NEON.cyan, alpha: 0.07 }),
  );

  // ── ONE neat rug grounds the WEST TV LOUNGE (sofa + its two flanking beanbags) as a single cosy zone.
  //    Centred on the west couch (~x100,1000), kept clear of the N-door lane mouth (x160..320). ────────
  const loungeX = left + 64; // ~104, the lounge axis hugging the west wall
  place(ctx, f.rug(art, 116, 188), loungeX, cy); // x[46..162], y[906..1094] — fully inside the walls

  // ── HERO ARCADE CABINET at the interactive anchor (240,1000); gaming chair pulled up at (320,1000)
  //    facing LEFT toward it — the play nook the affordances line up with. The N-door lane (x160..320)
  //    stays clear above: the cabinet body tops out ~y936, leaving the doorway threshold open. ────────
  place(ctx, arcadeCabinet(art, mix, lighten, darken, NEON.pink), cx, cy, 'arcade-cabinet');
  place(ctx, gamingChair(art, mix, lighten, darken, NEON.cyan), 320, cy, 'gaming-chair'); // SEAT: facing WEST

  // ── WEST TV LOUNGE: a neon sign + console TV high on the west wall with a plump low sofa parked in
  //    front, flanked by two tidy beanbags — all on the rug, all left of x≈160 so it never crowds the
  //    N-door lane (x160..320). The sofa + both beanbags are SITTABLE seats facing the screen. ─────────
  place(ctx, neonSign(80), loungeX, top + 36); // bolted high on the west wall, body inside x[54..146]
  place(ctx, f.gameConsole(art), loungeX, top + 76); // screen tops out ~y894, clear inside the wall
  place(ctx, f.sofa(art), loungeX, cy); // SEAT: couch on the rug, facing the TV (west)
  place(ctx, beanbag(lighten, darken, NEON.gold), loungeX + 8, cy - 48, 'beanbag-gold'); // SEAT: north flop, on the rug
  place(ctx, beanbag(lighten, darken, NEON.violet), loungeX + 8, cy + 48, 'beanbag-violet'); // SEAT: south flop, on the rug

  // ── GAMES TABLE: a single air-hockey table squared up below the arcade nook, clear of both doors.
  //    Footprint ~x165..315, y1068..1144 — a comfortable margin off the south wall (1160). ─────────────
  place(ctx, airHockey(art, mix, lighten, darken, 150, 76), cx, bottom - 54, 'air-hockey');

  // ── TWO neon WALL POSTERS, tidily aligned high in the top-RIGHT corner (right of the N door, x>320)
  //    so décor frames the arcade without crowding the door lane. ──────────────────────────────────────
  place(ctx, f.wallArt(art, 'poster'), right - 96, top + 32); // body x[322..366] y[856..880], inside
  place(ctx, f.wallArt(art, 'abstract'), right - 44, top + 32); // body x[374..418] y[856..880], inside

  // ── ONE tidy string of FAIRY LIGHTS high on the WEST wall, framing the TV lounge (off all doorways). ─
  place(ctx, fairyLights(darken, 110), loungeX, top + 14); // x[49..159], bulbs sag to ~y868, inside

  // ── Exactly TWO plants, one per BACK corner (deliberate, off the door lanes — no scattered greenery). ─
  place(ctx, f.plant(art, 'leafy'), left + 36, bottom - 44); // SW corner
  place(ctx, f.plant(art, 'tall'), right - 36, bottom - 40); // SE corner, below the E-door band
};

export default decorate;
